import { useState, useEffect } from 'react';
import { ShieldCheck, X, Trash2 } from 'lucide-react';
import DataTable from '../common/DataTable';
import type { Column } from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';
import DeploymentPasswordModal from './DeploymentPasswordModal';
import { useToast } from '../common/ToastContainer';
import { edgeService } from '../../services';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import configTemplateService from '../../services/configTemplate.service';
import type { ConfigTemplate } from '../../services/configTemplate.service';

interface PendingEdge {
  id: string;
  guid: string;
  mac_address: string;
  tenant_id: string;
  edge_name: string | null;
  edge_model: string | null;
  firmware_version: string | null;
  status: string;
  registered_at: string;
  deployment_password?: string;
  approved_by?: string;
  approved_at?: string;
}

const PendingEdgesTable = () => {
  const toast = useToast();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [pendingEdges, setPendingEdges] = useState<PendingEdge[]>([]);
  const [loading, setLoading] = useState(false);
  const [approvingGuid, setApprovingGuid] = useState<string | null>(null);
  const [rejectConfirm, setRejectConfirm] = useState<{ show: boolean; edge?: PendingEdge }>({ show: false });
  const [rejecting, setRejecting] = useState(false);
  const [deploymentPassword, setDeploymentPassword] = useState<{ password: string; edgeName: string } | null>(null);
  const [templates, setTemplates] = useState<ConfigTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('default');
  const [showTemplateDialog, setShowTemplateDialog] = useState<{ show: boolean; edge?: PendingEdge }>({ show: false });
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; edge?: PendingEdge }>({ show: false });
  const [deletingOne, setDeletingOne] = useState(false);
  const [clearAllConfirm, setClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  const loadPendingEdges = async () => {
    try {
      setLoading(true);
      const response = await edgeService.listPendingEdges();
      setPendingEdges(response.edges || []);
    } catch (error) {
      console.error('Failed to load pending edges:', error);
      toast.error('Load Failed', 'Failed to load pending edges');
    } finally {
      setLoading(false);
    }
  };

  // Load templates and pending edges on mount
  useEffect(() => {
    loadPendingEdges();
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const templateList = await configTemplateService.list({ edgeModel: 'VSR1000' });
      setTemplates(templateList);
      // Set default template if available
      const defaultTemplate = templateList.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleApprove = async (edge: PendingEdge, templateId?: string) => {
    try {
      setApprovingGuid(edge.guid);
      const response = await edgeService.approveEdge(edge.guid, 'admin', templateId || selectedTemplate !== 'default' ? selectedTemplate : undefined);
      
      // Show deployment password modal
      setDeploymentPassword({
        password: response.deploymentPassword,
        edgeName: edge.edge_name || edge.guid.substring(0, 8),
      });
      
      toast.success('Edge Approved', `Edge "${edge.edge_name || edge.guid.substring(0, 8)}" has been approved`);
      
      // Reload pending edges (will now show as approved with password)
      await loadPendingEdges();
      setShowTemplateDialog({ show: false });
    } catch (error: any) {
      console.error('Failed to approve edge:', error);
      toast.error('Approval Failed', error.message || 'Failed to approve edge');
    } finally {
      setApprovingGuid(null);
    }
  };

  const handleApproveClick = (edge: PendingEdge) => {
    if (templates.length > 0) {
      setShowTemplateDialog({ show: true, edge });
    } else {
      // No templates available, approve with default
      handleApprove(edge);
    }
  };

  const handleReject = async () => {
    if (!rejectConfirm.edge) return;

    try {
      setRejecting(true);
      await edgeService.rejectEdge(rejectConfirm.edge.guid, 'Rejected by administrator');
      toast.success('Edge Rejected', `Edge "${rejectConfirm.edge.edge_name || rejectConfirm.edge.guid.substring(0, 8)}" has been rejected`);
      setRejectConfirm({ show: false });
      await loadPendingEdges();
    } catch (error: any) {
      console.error('Failed to reject edge:', error);
      toast.error('Rejection Failed', error.message || 'Failed to reject edge');
    } finally {
      setRejecting(false);
    }
  };

  const handleClearAll = async () => {
    try {
      setClearingAll(true);
      const response = await edgeService.clearPendingEdges();
      toast.success('Pending Edges Cleared', `Cleared ${response.deletedCount} pending/approved edge(s)`);
      setClearAllConfirm(false);
      await loadPendingEdges();
    } catch (error: any) {
      console.error('Failed to clear pending edges:', error);
      toast.error('Clear Failed', error.message || 'Failed to clear pending edges');
    } finally {
      setClearingAll(false);
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteConfirm.edge) return;
    try {
      setDeletingOne(true);
      await edgeService.deletePendingEdge(deleteConfirm.edge.guid);
      toast.success('Edge Deleted', `Edge "${deleteConfirm.edge.edge_name || deleteConfirm.edge.guid.substring(0, 8)}" has been deleted`);
      setDeleteConfirm({ show: false });
      await loadPendingEdges();
    } catch (error: any) {
      toast.error('Delete Failed', error.message || 'Failed to delete edge');
    } finally {
      setDeletingOne(false);
    }
  };

  const columns: Column<PendingEdge>[] = [
    {
      key: 'guid',
      label: 'Edge GUID',
      sortable: true,
      render: (edge) => (
        <div className="font-mono text-sm">
          <div className="text-gray-900">{edge.guid.substring(0, 8)}...</div>
          <div className="text-xs text-gray-500">{edge.guid.substring(24)}</div>
        </div>
      ),
    },
    {
      key: 'edge_name',
      label: 'Edge Name',
      sortable: true,
      render: (edge) => (
        <span className="font-medium text-gray-900">
          {edge.edge_name || '-'}
        </span>
      ),
    },
    {
      key: 'mac_address',
      label: 'MAC Address',
      sortable: true,
      render: (edge) => (
        <span className="font-mono text-sm text-gray-700">{edge.mac_address}</span>
      ),
    },
    {
      key: 'edge_model',
      label: 'Model',
      sortable: true,
      render: (edge) => edge.edge_model || '-',
    },
    {
      key: 'firmware_version',
      label: 'Firmware',
      sortable: true,
      render: (edge) => edge.firmware_version || '-',
    },
    {
      key: 'registered_at',
      label: 'Registered At',
      sortable: true,
      render: (edge) => formatDate(edge.registered_at, dateSettings),
    },
    {
      key: 'status',
      label: 'Status',
      render: (edge) => (
        <div>
          {edge.status === 'pending_approval' ? (
            <StatusBadge status="Pending" />
          ) : edge.status === 'approved' ? (
            <div>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Approved
              </span>
              {edge.approved_by && (
                <div className="text-xs text-gray-500 mt-1">
                  by {edge.approved_by}
                </div>
              )}
            </div>
          ) : (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {edge.status}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (edge) => (
        <div className="flex items-center space-x-2">
          {edge.status === 'pending_approval' ? (
            <>
              <button
                onClick={() => handleApproveClick(edge)}
                disabled={approvingGuid === edge.guid}
                className="p-1.5 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                title="Approve Edge"
              >
                <ShieldCheck className={`w-5 h-5 ${approvingGuid === edge.guid ? 'text-gray-400' : 'text-green-600 hover:text-green-700'}`} />
              </button>
              <button
                onClick={() => setRejectConfirm({ show: true, edge })}
                className="p-1.5 hover:bg-red-50 rounded transition-colors"
                title="Reject Edge"
              >
                <X className="w-5 h-5 text-red-600 hover:text-red-700" />
              </button>
            </>
          ) : edge.status === 'approved' && edge.deployment_password ? (
            <button
              onClick={() => setDeploymentPassword({
                password: edge.deployment_password!,
                edgeName: edge.edge_name || edge.guid.substring(0, 8),
              })}
              className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              title="View Deployment Password"
            >
              View Password
            </button>
          ) : null}
          <button
            onClick={() => setDeleteConfirm({ show: true, edge })}
            className="p-1.5 hover:bg-red-50 rounded transition-colors"
            title="Delete Edge"
          >
            <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {pendingEdges.length > 0 && (
            <span>{pendingEdges.length} pending/approved edge(s)</span>
          )}
        </div>
        {pendingEdges.length > 0 && (
          <button
            onClick={() => setClearAllConfirm(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Clear All</span>
          </button>
        )}
      </div>
      
      <DataTable
        columns={columns}
        data={pendingEdges}
        loading={loading}
        emptyMessage="No pending edge registrations"
      />

      <ConfirmDialog
        isOpen={rejectConfirm.show}
        title="Reject Edge Registration"
        message={`Are you sure you want to reject edge "${rejectConfirm.edge?.edge_name || rejectConfirm.edge?.guid.substring(0, 8)}"?\n\nThe edge will not be able to provision certificates and will need to re-register.`}
        confirmText="Reject"
        variant="danger"
        onConfirm={handleReject}
        onClose={() => setRejectConfirm({ show: false })}
        isLoading={rejecting}
      />

      <DeploymentPasswordModal
        isOpen={!!deploymentPassword}
        onClose={() => setDeploymentPassword(null)}
        deploymentPassword={deploymentPassword?.password || ''}
        edgeName={deploymentPassword?.edgeName || ''}
      />

      {showTemplateDialog.show && showTemplateDialog.edge && (
        <ConfirmDialog
          isOpen={showTemplateDialog.show}
          title="Approve Edge with Configuration Template"
          message={
            <div className="space-y-4">
              <p>Select a configuration template to apply when approving this edge:</p>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="default">Default Template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} {template.is_default ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          }
          confirmText="Approve"
          variant="info"
          onConfirm={() => handleApprove(showTemplateDialog.edge!, selectedTemplate !== 'default' ? selectedTemplate : undefined)}
          onClose={() => setShowTemplateDialog({ show: false })}
          isLoading={approvingGuid === showTemplateDialog.edge?.guid}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete Pending Edge"
        message={`Are you sure you want to delete edge "${deleteConfirm.edge?.edge_name || deleteConfirm.edge?.guid.substring(0, 8)}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteOne}
        onClose={() => setDeleteConfirm({ show: false })}
        isLoading={deletingOne}
      />

      <ConfirmDialog
        isOpen={clearAllConfirm}
        title="Clear All Pending Edges"
        message="Are you sure you want to clear all pending and approved edges? This will permanently delete all edge records in pending_approval or approved status. This action cannot be undone."
        confirmText="Clear All"
        variant="danger"
        onConfirm={handleClearAll}
        onClose={() => setClearAllConfirm(false)}
        isLoading={clearingAll}
      />
    </>
  );
};

export default PendingEdgesTable;

